import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Stack, TextField, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { updateProfile } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useUserOrganizations } from '../../assistants/drew/hooks/useOrganization';
import { ROLE_DEFINITIONS, membersFromOrgDoc } from '../../services/roleService';
import { updateMemberProfile } from '../../services/orgUserService';
import { memberUnitLabel } from '../../assistants/drew/components/members/memberForm';
import { prepareAvatar } from '../../utils/imageResize';

/**
 * Your Settings → Profile (Beta-1 plan P4-27, TICK A36): first / last / display name and title
 * (editable), email, photo (client-resized to 256 px WebP ≤ 200 KB, stored at
 * avatars/{uid}/avatar.webp and mirrored to Auth photoURL + membersIndex through the
 * `hugoUpdateOrgMember` profile action), and read-only membership rows per organization —
 * role label, site, department, NWT.
 */
export default function ProfileSection() {
  const { user } = useAuth();
  const { profile } = useUser();
  const { notify } = useNotification();
  const { organizations } = useUserOrganizations(user?.uid);
  const fileRef = useRef(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', displayName: '', title: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoURL, setPhotoURL] = useState(user?.photoURL || '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const access = await getDoc(doc(db, 'Hugo_ToolAccess', user.uid));
        const d = access.exists() ? access.data() : {};
        const display = d.displayName || user.displayName || '';
        const [first, ...rest] = display.split(/\s+/);
        if (!cancelled) {
          setForm({ firstName: d.firstName || first || '', lastName: d.lastName || rest.join(' ') || '', displayName: display, title: d.title || '' });
          setPhotoURL(d.photoURL || user.photoURL || '');
        }
      } catch {
        if (!cancelled) setForm({ firstName: '', lastName: '', displayName: user.displayName || '', title: '' });
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const memberships = organizations.map((org) => ({ org, me: membersFromOrgDoc(org).find((m) => m.uid === user?.uid) || null }));

  const auto = (f) => `${f.firstName} ${f.lastName}`.trim();
  const setName = (key, value) =>
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (!f.displayName || f.displayName === auto(f)) next.displayName = auto(next);
      return next;
    });

  const save = async () => {
    if (!form.displayName.trim()) { setError('Display name cannot be empty'); return; }
    setError('');
    setSaving(true);
    try {
      await updateProfile(user, { displayName: form.displayName.trim() });
      await setDoc(doc(db, 'Hugo_UserPreferences', user.uid), { displayName: form.displayName.trim(), email: user.email, updatedAt: serverTimestamp() }, { merge: true });
      try {
        await updateMemberProfile({ uid: user.uid, firstName: form.firstName.trim(), lastName: form.lastName.trim(), displayName: form.displayName.trim(), title: form.title.trim() });
      } catch (err) {
        console.warn('Profile mirror to organizations failed (function not deployed?):', err.message);
      }
      notify('Profile saved', 'success');
    } catch (err) {
      console.error(err);
      notify('Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    setUploading(true);
    setError('');
    try {
      const blob = await prepareAvatar(file);
      const r = storageRef(storage, `avatars/${user.uid}/avatar.webp`);
      await uploadBytes(r, blob, { contentType: blob.type, cacheControl: 'public,max-age=3600' });
      const url = `${await getDownloadURL(r)}&v=${Date.now()}`;
      await updateProfile(user, { photoURL: url });
      await setDoc(doc(db, 'Hugo_UserPreferences', user.uid), { photoURL: url, updatedAt: serverTimestamp() }, { merge: true });
      try { await updateMemberProfile({ uid: user.uid, photoURL: url }); } catch { /* mirror best-effort */ }
      setPhotoURL(url);
      notify('Photo updated', 'success');
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not upload the photo.');
    } finally {
      setUploading(false);
    }
  };

  const initials = (form.displayName || user?.email || '?').split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2);

  return (
    <Card sx={{ maxWidth: 600, mt: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">Profile</Typography>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Avatar src={photoURL || undefined} sx={{ width: 72, height: 72, fontSize: '1.4rem', bgcolor: 'secondary.main' }}>{initials}</Avatar>
          <Box>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickPhoto} data-testid="avatar-file" />
            <Button size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <PhotoCameraIcon />} onClick={() => fileRef.current?.click()} disabled={uploading}>
              {photoURL ? 'Change photo' : 'Add photo'}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Shown in the top bar, member lists and activity. Resized to 256 px automatically.
            </Typography>
          </Box>
        </Stack>
        {error && <Typography variant="body2" color="error" sx={{ mb: 1 }}>{error}</Typography>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap sx={{ mb: 2 }}>
          <TextField label="First name" fullWidth size="small" value={form.firstName} onChange={(e) => setName('firstName', e.target.value)} />
          <TextField label="Last name" fullWidth size="small" value={form.lastName} onChange={(e) => setName('lastName', e.target.value)} />
        </Stack>
        <TextField label="Display name" fullWidth size="small" value={form.displayName} onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))} sx={{ mb: 2 }} aria-label="Display name" />
        <TextField label="Title / position" fullWidth size="small" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="e.g. Operations Manager" sx={{ mb: 2 }} />
        <TextField label="Email" fullWidth size="small" value={user?.email || ''} disabled sx={{ mb: 2 }} aria-label="Email address" />

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Your organizations</Typography>
        {memberships.length === 0 && <Typography variant="body2" color="text.secondary">No organization yet.</Typography>}
        <Stack spacing={1} sx={{ mb: 2 }}>
          {memberships.map(({ org, me }) => (
            <Box key={org.id} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>{org.name}</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5, flexWrap: 'wrap' }} useFlexGap>
                <Chip size="small" label={me ? ROLE_DEFINITIONS[me.role]?.label || me.role : (org.admins || []).includes(user?.uid) ? 'Org Admin' : 'Member'} color="primary" variant="outlined" />
                {me && memberUnitLabel(me, org) && <Typography variant="caption" color="text.secondary">{memberUnitLabel(me, org)}</Typography>}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>Role and unit are set by your organization&apos;s administrator.</Typography>
            </Box>
          ))}
        </Stack>
        {profile?.uid && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>User ID: {profile.uid}</Typography>}
        <Button variant="contained" onClick={save} disabled={saving} aria-label="Save profile">
          {saving ? <CircularProgress size={20} /> : 'Save Profile'}
        </Button>
      </CardContent>
    </Card>
  );
}
